import React from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  Send,
  Shield,
  Award,
  Users,
  Clock,
  ChevronRight,
} from "lucide-react";

const Footer = () => {
  const examCategories = [
    "Government Exams",
    "Banking & SSC",
    "UPSC & State PSC",
    "Engineering (GATE, JEE)",
    "Management (CAT, XAT)",
    "Defence Exams",
    "Teaching Exams",
    "Medical (NEET, AIIMS)",
    "Law Entrance",
    "International Exams",
  ];

  const resources = [
    "Blog & Articles",
    "Study Materials",
    "Question Banks",
    "Previous Year Papers",
    "Exam Syllabus",
    "Preparation Tips",
    "Success Stories",
    "Webinars",
    "Career Guidance",
    "Scholarship Tests",
  ];

  const company = [
    "About Us",
    "Careers",
    "Press & Media",
    "Partners",
    "Testimonials",
    "Awards",
    "Contact Us",
    "Privacy Policy",
    "Terms of Service",
    "Refund Policy",
  ];

  const features = [
    { icon: Shield, label: "100% Secure Platform" },
    { icon: Award, label: "Award Winning" },
    { icon: Users, label: "Trusted by 500K+" },
    { icon: Clock, label: "24/7 Support" },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Top Section */}
      <div className="border-b border-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-4">
            {/* Trust Features */}
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 rounded-xl bg-gray-800/30 p-4 backdrop-blur-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-purple-600">
                  <feature.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold">{feature.label}</p>
                  <p className="text-sm text-gray-400">Certified & Verified</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600">
                <Award className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">TestSetu</h2>
                <p className="text-sm text-gray-400">Smarter than TestBook</p>
              </div>
            </div>
            <p className="mb-8 text-gray-400">
              India's most intelligent test preparation platform, transforming
              aspirants into top performers with AI-powered analytics and real
              exam simulation.
            </p>

            {/* Newsletter */}
            <div className="mb-8">
              <h3 className="mb-4 font-semibold">Stay Updated</h3>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                />
                <button className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 font-semibold hover:shadow-lg">
                  <Send className="h-4 w-4" />
                  <span className="hidden sm:inline">Subscribe</span>
                </button>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="mb-4 font-semibold">Follow Us</h3>
              <div className="flex gap-3">
                {[
                  { icon: Facebook, label: "Facebook" },
                  { icon: Twitter, label: "Twitter" },
                  { icon: Linkedin, label: "LinkedIn" },
                  { icon: Instagram, label: "Instagram" },
                  { icon: Youtube, label: "YouTube" },
                ].map((social, index) => (
                  <a
                    key={index}
                    href="#"
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-800 transition-all hover:bg-blue-600 hover:shadow-lg"
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links Columns */}
          <div>
            <h3 className="mb-6 text-lg font-semibold">Exam Categories</h3>
            <ul className="space-y-3">
              {examCategories.slice(0, 6).map((category, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="group flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
                  >
                    <ChevronRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                    {category}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-6 text-lg font-semibold">Resources</h3>
            <ul className="space-y-3">
              {resources.slice(0, 6).map((resource, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="group flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
                  >
                    <ChevronRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                    {resource}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-6 text-lg font-semibold">Company</h3>
            <ul className="space-y-3">
              {company.slice(0, 6).map((item, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="group flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
                  >
                    <ChevronRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                    {item}
                  </a>
                </li>
              ))}
            </ul>

            {/* Contact Info */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-400" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-gray-400">support@testsetu.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-green-400" />
                <div>
                  <p className="text-sm font-medium">Phone</p>
                  <p className="text-sm text-gray-400">+91 1800-123-4567</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Apps */}
        <div className="mt-16 rounded-2xl bg-gradient-to-r from-gray-800 to-gray-900 p-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 text-2xl font-bold">Learn On The Go</h3>
              <p className="mb-6 text-gray-400">
                Download our mobile app for mock tests, instant results, and
                performance tracking anywhere, anytime.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="flex items-center gap-3 rounded-xl bg-black px-6 py-3 hover:bg-gray-900">
                  <div className="text-2xl">📱</div>
                  <div className="text-left">
                    <p className="text-xs text-gray-400">Download on</p>
                    <p className="font-semibold">App Store</p>
                  </div>
                </button>
                <button className="flex items-center gap-3 rounded-xl bg-black px-6 py-3 hover:bg-gray-900">
                  <div className="text-2xl">🤖</div>
                  <div className="text-left">
                    <p className="text-xs text-gray-400">Get it on</p>
                    <p className="font-semibold">Google Play</p>
                  </div>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                {/* App Mockup */}
                <div className="h-48 w-48 rounded-3xl border-8 border-gray-700 bg-gradient-to-br from-blue-900/20 to-purple-900/20 backdrop-blur-sm" />
                <div className="absolute -right-4 -top-4 h-32 w-32 rounded-2xl border-4 border-gray-600 bg-gradient-to-br from-gray-800 to-gray-900" />
                <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-xl border-4 border-gray-600 bg-gradient-to-br from-gray-800 to-gray-900" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 bg-gray-900/50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="text-center md:text-left">
              <p className="text-gray-400">
                © 2024 TestSetu. All rights reserved.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Made with ❤️ for India's aspirants
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Privacy Policy
              </a>
              <Link
                to="/delete-accounts"
                className="text-gray-400 transition-colors hover:text-red-400"
              >
                Delete Accounts
              </Link>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Terms of Service
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Cookie Policy
              </a>
              <a
                href="#"
                className="text-gray-400 transition-colors hover:text-white"
              >
                Sitemap
              </a>
            </div>
          </div>

          {/* Disclosure */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              TestSetu is an independent test preparation platform and is not
              affiliated with any government agency or educational institution.
              All exam names are trademarks of their respective owners.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
